import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { analytics } from '../services/analytics';
import { logger } from '../services/logger';
import { Profile } from '../types';

interface SubscriptionInfo {
  currentPlan: string;
  trialEndsAt: Date | null;
  isTrialExpired: boolean;
  needsPayment: boolean;
  status: string;
  periodEndsAt: Date | null;
  cancelAtPeriodEnd: boolean;
}

export function useSubscription() {
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({
    currentPlan: 'basic',
    trialEndsAt: null,
    isTrialExpired: false,
    needsPayment: false,
    status: 'inactive',
    periodEndsAt: null,
    cancelAtPeriodEnd: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSubscription();
  }, []);

  async function loadSubscription() {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) return;

      // Directly await the full query chain
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          subscription_tier,
          subscription_status,
          subscription_trial_end,
          subscription_period_end,
          subscription_cancel_at_period_end
        `)
        .eq('id', user.id)
        .single();

      if (profileError) {
        logger.error('Error loading subscription:', profileError);
        return;
      }

      if (profile) {
        const typedProfile = profile as Profile;
        const trialEndsAt = typedProfile.subscription_trial_end ? new Date(typedProfile.subscription_trial_end) : null;
        const isTrialExpired = trialEndsAt ? trialEndsAt < new Date() : false;
        const needsPayment = isTrialExpired && typedProfile.subscription_status !== 'active';
        const periodEndsAt = typedProfile.subscription_period_end ? new Date(typedProfile.subscription_period_end) : null;

        setSubscriptionInfo({
          currentPlan: typedProfile.subscription_tier || 'basic',
          trialEndsAt,
          isTrialExpired,
          needsPayment,
          status: typedProfile.subscription_status || 'inactive',
          periodEndsAt,
          cancelAtPeriodEnd: typedProfile.subscription_cancel_at_period_end || false
        });

        // Track subscription status
        analytics.track('subscription_status', {
          plan: typedProfile.subscription_tier,
          status: typedProfile.subscription_status,
          isTrialExpired,
          needsPayment
        });
      }
    } catch (err: any) {
      logger.error('Error loading subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function changePlan(newPlan: string, customPrice?: number) {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('No user found');

      // Update subscription tier immediately for better UX - directly await the full query chain
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          subscription_tier: newPlan,
          subscription_status: 'active',
          subscription_trial_end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Track plan change
      analytics.track('subscription_changed', {
        fromPlan: subscriptionInfo.currentPlan,
        toPlan: newPlan,
        customPrice
      });

      await loadSubscription();
    } catch (err: any) {
      logger.error('Error changing plan:', err);
      throw err;
    }
  }

  return {
    ...subscriptionInfo,
    loading,
    error,
    refresh: loadSubscription,
    changePlan
  };
}